// These public/embed calls are hand-written on the raw `apiClient` rather than
// taken from the generated SDK on purpose: the widget authenticates with an
// embed token (`x-embed-token` header for signed JWTs, `token` query param for
// anonymous ids) that the generated client can't express, and the generated
// client's shared interceptor redirects to login on 401 — wrong for an
// anonymous public visitor. `buildPublicAuth` centralises that auth shape.
import {
  AddMessageData,
  AddMessageRequest,
  Assistant,
  ConversationWithMessages,
  CsatFeedback,
  PageConversation,
  PublicAssistant,
} from "@/generated";
import apiClient from "../api-client";

export type PublicAuth = {
  spotGroupId: string;
  assistantId: string;
  token: string;
  deploymentId?: string;
  authToken?: string;
};

type PublicAddMessageData = Omit<AddMessageData, "path" | "query"> & {
  path: {
    conversation_id: string;
    group_id: string;
  };
  query?: {
    assistant_id?: string;
    token?: string;
    message_id?: string | null;
    is_final?: boolean;
    deployment_id?: string;
  };
};

// A signed embed JWT is a credential — send it in the `x-embed-token` header
// (out of URLs/logs); the anonymous public id stays a query param.
export const isSignedEmbedToken = (token: string): boolean =>
  /^eyJ[\w-]*\.[\w-]+\.[\w-]*$/.test(token);

// noxus_user embeds authenticate as the real Noxus user via a relayed access
// token, sent per-call as a Bearer header (not a global axios default) so it
// can't leak onto unrelated requests and there's no render/effect ordering race.
export const buildPublicAuth = (
  token: string,
  assistantId: string,
  deploymentId?: string,
  authToken?: string
): { query: string; headers: Record<string, string> } => {
  const params = new URLSearchParams({ assistant_id: assistantId });
  if (deploymentId) params.set("deployment_id", deploymentId);
  const headers: Record<string, string> = {};
  if (isSignedEmbedToken(token)) {
    headers["x-embed-token"] = token;
  } else {
    params.set("token", token);
  }
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
  return { query: params.toString(), headers };
};

const getConversation = async ({
  spotGroupId,
  conversationId,
  assistantId,
  token,
  deploymentId,
  authToken,
  paged,
  before,
}: {
  spotGroupId: string;
  conversationId: string;
  assistantId: string;
  token: string;
  deploymentId?: string;
  authToken?: string;
  paged?: boolean;
  before?: string;
}) => {
  const { query, headers } = buildPublicAuth(
    token,
    assistantId,
    deploymentId,
    authToken
  );
  const params = new URLSearchParams(query);
  if (paged) params.set("paged", "true");
  if (before) params.set("before", before);
  return apiClient
    .get(
      `/api/public/groups/${spotGroupId}/conversation/${conversationId}?${params.toString()}`,
      { headers }
    )
    .then((res) => res.data as ConversationWithMessages);
};

const listConversations = async ({
  spotGroupId,
  assistantId,
  token,
  deploymentId,
  authToken,
}: {
  spotGroupId: string;
  assistantId: string;
  token: string;
  deploymentId?: string;
  authToken?: string;
}) => {
  const { query, headers } = buildPublicAuth(
    token,
    assistantId,
    deploymentId,
    authToken
  );
  return apiClient
    .get(`/api/public/groups/${spotGroupId}/conversation?${query}`, { headers })
    .then((res) => res.data as PageConversation);
};

const addMessage = async (options: PublicAddMessageData) => {
  const { path, body, query } = options;
  const assistantId = query?.assistant_id || "";
  const token = query?.token || "";
  const deploymentId = query?.deployment_id;
  const { query: q, headers } = buildPublicAuth(
    token,
    assistantId,
    deploymentId
  );

  return apiClient.post<ConversationWithMessages>(
    `/api/public/groups/${path.group_id}/conversation/${path.conversation_id}?${q}`,
    body,
    { headers }
  );
};

const createConversation = async ({
  assistantId,
  token,
  spotGroupId,
  deploymentId,
  authToken,
  variables,
}: {
  assistantId: string;
  token: string;
  spotGroupId: string;
  deploymentId?: string;
  authToken?: string;
  variables?: Record<string, string>;
}) => {
  const { query, headers } = buildPublicAuth(
    token,
    assistantId,
    deploymentId,
    authToken
  );
  return apiClient.post(
    `/api/public/groups/${spotGroupId}/conversation?${query}`,
    variables ? { variables } : {},
    { headers }
  );
};

const addMessageFeedback = async ({
  spotGroupId,
  conversationId,
  messageId,
  assistantId,
  token,
  deploymentId,
  authToken,
  feedbackType,
  comment,
}: {
  spotGroupId: string;
  conversationId: string;
  messageId: string;
  assistantId: string;
  token: string;
  deploymentId?: string;
  authToken?: string;
  feedbackType: string;
  comment?: string;
}) => {
  const { query, headers } = buildPublicAuth(
    token,
    assistantId,
    deploymentId,
    authToken
  );
  const params = new URLSearchParams(query);
  return apiClient.post(
    `/api/public/groups/${spotGroupId}/conversation/${conversationId}/messages/${messageId}/feedback?${params.toString()}`,
    { feedback_type: feedbackType, comment },
    { headers }
  );
};

const removeMessageFeedback = async ({
  spotGroupId,
  conversationId,
  messageId,
  assistantId,
  token,
  deploymentId,
  authToken,
}: {
  spotGroupId: string;
  conversationId: string;
  messageId: string;
  assistantId: string;
  token: string;
  deploymentId?: string;
  authToken?: string;
}) => {
  const { query, headers } = buildPublicAuth(
    token,
    assistantId,
    deploymentId,
    authToken
  );
  const params = new URLSearchParams(query);
  return apiClient.delete(
    `/api/public/groups/${spotGroupId}/conversation/${conversationId}/messages/${messageId}/feedback?${params.toString()}`,
    { headers }
  );
};

const getAssistant = async ({
  spotGroupId,
  assistantId,
  token,
  deploymentId,
  authToken,
}: {
  spotGroupId: string;
  assistantId: string;
  token: string;
  deploymentId?: string;
  authToken?: string;
}) => {
  const { query, headers } = buildPublicAuth(
    token,
    assistantId,
    deploymentId,
    authToken
  );
  return apiClient.get<PublicAssistant>(
    `/api/public/groups/${spotGroupId}/assistants/${assistantId}/public?${query}`,
    { headers }
  );
};

const stopConversation = async ({
  spotGroupId,
  conversationId,
  token,
  assistantId,
  deploymentId,
  authToken,
}: PublicAuth & { conversationId: string }) => {
  const { query, headers } = buildPublicAuth(
    token,
    assistantId,
    deploymentId,
    authToken
  );
  return apiClient.post(
    `/api/public/groups/${spotGroupId}/conversation/${conversationId}/stop?${query}`,
    undefined,
    { headers }
  );
};

const addCsatResponse = async ({
  spotGroupId,
  conversationId,
  token,
  assistantId,
  deploymentId,
  authToken,
  rating,
  comment,
}: PublicAuth & { conversationId: string } & CsatFeedback) => {
  const { query, headers } = buildPublicAuth(
    token,
    assistantId,
    deploymentId,
    authToken
  );
  return apiClient.post(
    `/api/public/groups/${spotGroupId}/conversation/${conversationId}/csat?${query}`,
    { rating, comment },
    { headers }
  );
};

const deferCsatResponse = async ({
  spotGroupId,
  conversationId,
  token,
  assistantId,
  deploymentId,
  authToken,
}: PublicAuth & { conversationId: string }) => {
  const { query, headers } = buildPublicAuth(
    token,
    assistantId,
    deploymentId,
    authToken
  );
  return apiClient.post(
    `/api/public/groups/${spotGroupId}/conversation/${conversationId}/csat/defer?${query}`,
    undefined,
    { headers }
  );
};

export const PublicConversationService = {
  getConversation,
  listConversations,
  addMessage,
  addMessageFeedback,
  removeMessageFeedback,
  createConversation,
  getAssistant,
  stopConversation,
  addCsatResponse,
  deferCsatResponse,
};
